import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Mail, User } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { auth } from '../firebase';

export const Login: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const googleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                return;
            }

            if (googleTimeoutRef.current) {
                clearTimeout(googleTimeoutRef.current);
                googleTimeoutRef.current = null;
            }

            setLoading(false);
            setError(null);
        });

        return () => {
            if (googleTimeoutRef.current) {
                clearTimeout(googleTimeoutRef.current);
            }
            unsubscribe();
        };
    }, []);

    const clearMessages = () => {
        setError(null);
        setNotice(null);
    };

    const mapAuthError = (message: string) => {
        if (message.includes('Invalid login credentials')) {
            return 'Invalid email or password.';
        }

        if (message.includes('Email not confirmed')) {
            return 'Please check your email to confirm your account.';
        }

        if (message.includes('User already registered')) {
            return 'This email is already registered. Try signing in instead.';
        }

        return message || 'Authentication failed.';
    };

    const handleGoogleLogin = async () => {
        clearMessages();
        setLoading(true);

        googleTimeoutRef.current = setTimeout(() => {
            googleTimeoutRef.current = null;
            setLoading(false);
            setError('Google login timed out. Please try again.');
        }, 15000);

        try {
            await auth.signInWithGoogle();
        } catch {
            if (googleTimeoutRef.current) {
                clearTimeout(googleTimeoutRef.current);
                googleTimeoutRef.current = null;
            }

            setLoading(false);
            setError('Unable to continue with Google. Please try again.');
        }
    };

    const handleEmailLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        clearMessages();
        setLoading(true);

        const timeoutId = setTimeout(() => {
            setLoading(false);
            setError('Request timed out. Please check your internet connection and try again.');
        }, 10000);

        try {
            if (isSignUp) {
                if (!displayName.trim()) {
                    clearTimeout(timeoutId);
                    throw new Error('Please enter your name.');
                }

                await auth.signUpWithEmail(email, password, displayName);
                clearTimeout(timeoutId);
                setLoading(false);
                setIsSignUp(false);
                setPassword('');
                setNotice('Account created. You can sign in now.');
                return;
            }

            await auth.signInWithEmail(email, password);
            clearTimeout(timeoutId);
        } catch (err: unknown) {
            clearTimeout(timeoutId);
            setLoading(false);
            const rawMessage = err instanceof Error ? err.message : String(err);
            setError(mapAuthError(rawMessage));
        }
    };

    const handleGuestLogin = async () => {
        clearMessages();
        setLoading(true);

        if (!Capacitor.isNativePlatform()) {
            localStorage.setItem('guestMode', 'true');
            window.location.href = '/';
            return;
        }

        let signInCompleted = false;

        const timeoutId = setTimeout(() => {
            if (signInCompleted) {
                return;
            }

            localStorage.setItem('guestMode', 'true');
            window.location.reload();
        }, 3000);

        try {
            await auth.signInAnonymously();
            signInCompleted = true;
            clearTimeout(timeoutId);
        } catch {
            signInCompleted = true;
            clearTimeout(timeoutId);
            localStorage.setItem('guestMode', 'true');
            window.location.reload();
        }
    };

    const handleResetPassword = async (event: React.FormEvent) => {
        event.preventDefault();
        clearMessages();
        setLoading(true);

        try {
            await auth.resetPassword(resetEmail);
            setResetEmail('');
            setShowResetPassword(false);
            setNotice('Password reset email sent. Check your inbox.');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to send reset email.');
        } finally {
            setLoading(false);
        }
    };

    if (showResetPassword) {
        return (
            <div className="min-h-screen bg-linear-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 w-full max-w-md"
                >
                    <div className="flex items-center gap-4 mb-6">
                        <img
                            src="/logo.png"
                            alt="AR Drawing Master"
                            className="w-14 h-14 rounded-2xl shadow-lg object-cover"
                            width={56}
                            height={56}
                        />
                        <div>
                            <h2 className="text-3xl font-black text-gray-900 dark:text-white">Reset Password</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">We'll email you a fresh password reset link.</p>
                        </div>
                    </div>

                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Email</label>
                            <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                    placeholder="your@email.com"
                                    autoComplete="email"
                                    name="reset-email"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-xl text-sm"
                            >
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Sending...' : 'Send Reset Email'}
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setShowResetPassword(false);
                                setError(null);
                            }}
                            className="w-full text-gray-600 dark:text-gray-400 py-2 text-sm"
                        >
                            Back to Sign In
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-linear-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4 overflow-hidden relative">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                    className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 w-full max-w-md relative z-10"
            >
                <div className="flex justify-center mb-6">
                    <img
                        src="/logo.png"
                        alt="AR Drawing Master"
                        className="w-20 h-20 rounded-3xl shadow-lg object-cover"
                        width={80}
                        height={80}
                    />
                </div>

                <h1 className="text-3xl font-black text-center text-gray-900 dark:text-white mb-2">
                    {isSignUp ? 'Create Account' : 'Welcome Back'}
                </h1>
                <p className="text-center text-gray-500 dark:text-gray-400 mb-8">
                    {isSignUp ? 'Join AR Drawing Master' : 'Sign in to continue creating'}
                </p>

                <form onSubmit={handleEmailLogin} className="space-y-4">
                    {isSignUp && (
                        <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Name</label>
                            <div className="relative mt-1">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                    placeholder="Your name"
                                    autoComplete="name"
                                    name="display-name"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Email</label>
                        <div className="relative mt-1">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                placeholder="your@email.com"
                                autoComplete={isSignUp ? 'email' : 'username'}
                                name="email"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Password</label>
                        <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="password"
                                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                                name="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                placeholder="Enter your password"
                                required
                                minLength={6}
                            />
                        </div>
                    </div>

                    {!isSignUp && (
                        <div className="text-right">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowResetPassword(true);
                                    clearMessages();
                                }}
                                className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                                Forgot password?
                            </button>
                        </div>
                    )}

                    {notice && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 p-3 rounded-xl text-sm"
                        >
                            {notice}
                        </motion.div>
                    )}

                    {error && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-xl text-sm"
                        >
                            {error}
                        </motion.div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
                        {!loading && <ArrowRight size={20} />}
                    </button>

                    {loading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 5 }}
                            className="text-center mt-4"
                        >
                            <p className="text-sm text-gray-500 mb-2">Taking longer than expected?</p>
                            <button
                                type="button"
                                onClick={() => {
                                    localStorage.setItem('guestMode', 'true');
                                    window.location.reload();
                                }}
                                className="text-indigo-600 font-bold hover:underline"
                            >
                                Skip & Enter App
                            </button>
                        </motion.div>
                    )}
                </form>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => {
                            setIsSignUp(!isSignUp);
                            clearMessages();
                        }}
                        className="text-gray-600 dark:text-gray-400 text-sm"
                    >
                        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                        <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                            {isSignUp ? 'Sign In' : 'Sign Up'}
                        </span>
                    </button>
                </div>

                <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white dark:bg-gray-800 text-gray-500">Or</span>
                    </div>
                </div>

                <button
                    onClick={handleGuestLogin}
                    disabled={loading}
                    className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-3 rounded-xl font-bold text-lg hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Continue as Guest
                </button>

                <div className="mt-4">
                    <button
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className="w-full bg-white text-gray-700 py-3 rounded-xl font-bold text-lg shadow-md border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                    </button>
                </div>
            </motion.div>
        </div>
    );
};
